/**
 * Fraîcheur des données — docs/05-data-freshness.md, ADR-0004.
 * Le statut est calculé par le CLIENT, à l'affichage, à partir d'horodatages absolus.
 */

export type FreshnessStatus = 'live' | 'recent' | 'stale' | 'unavailable';

export type DataKind =
  | 'departures'
  | 'disruptions'
  | 'weather'
  | 'nowcast'
  | 'sky'
  | 'iss'
  | 'airQuality'
  | 'water'
  | 'alerts'
  | 'events'
  | 'news'
  | 'roadworks'
  | 'places';

export interface FreshnessMeta {
  readonly fetchedAt: string | null;
  readonly observedAt: string | null;
  readonly expiresAt: string | null;
}

export interface FreshnessPolicy {
  /** Durée de cache côté Worker (mutualisée entre utilisateurs). */
  readonly cacheTtlMs: number;
  /** Intervalle de rafraîchissement côté client. */
  readonly refreshMs: number;
  readonly liveMs: number;
  readonly recentMs: number;
  /** Au-delà, la donnée n'est plus affichée du tout. */
  readonly discardAfterMs: number;
  /**
   * Intervalle minimal entre deux écritures de la dernière valeur connue dans KV.
   * Par défaut, le TTL de cache. Relevé pour les données très rafraîchies afin de tenir dans
   * l'offre Cloudflare gratuite (ADR-0005) : la mémoire de l'isolat prend le relais.
   */
  readonly kvWriteIntervalMs?: number;
}

const SEC = 1_000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Valeurs initiales justifiées dans docs/05 §3 — à ajuster après observation réelle. */
export const FRESHNESS_POLICIES: Readonly<Record<DataKind, FreshnessPolicy>> = {
  departures: {
    cacheTtlMs: 60 * SEC,
    refreshMs: 30 * SEC,
    liveMs: 2 * MIN,
    recentMs: 5 * MIN,
    discardAfterMs: 15 * MIN,
    kvWriteIntervalMs: 15 * MIN,
  },
  disruptions: {
    cacheTtlMs: 5 * MIN,
    refreshMs: 2 * MIN,
    liveMs: 10 * MIN,
    recentMs: 30 * MIN,
    discardAfterMs: DAY,
    kvWriteIntervalMs: 30 * MIN,
  },
  weather: {
    cacheTtlMs: 10 * MIN,
    refreshMs: 5 * MIN,
    liveMs: 30 * MIN,
    recentMs: 90 * MIN,
    discardAfterMs: 6 * HOUR,
    kvWriteIntervalMs: 30 * MIN,
  },
  nowcast: {
    cacheTtlMs: 10 * MIN,
    refreshMs: 5 * MIN,
    liveMs: 20 * MIN,
    recentMs: 45 * MIN,
    discardAfterMs: 90 * MIN,
    kvWriteIntervalMs: 30 * MIN,
  },
  // Prévision horaire : le modèle Météo-France est recalculé toutes les heures au mieux.
  sky: {
    cacheTtlMs: 30 * MIN,
    refreshMs: 15 * MIN,
    liveMs: 90 * MIN,
    recentMs: 3 * HOUR,
    discardAfterMs: 12 * HOUR,
    kvWriteIntervalMs: HOUR,
  },
  // Éléments orbitaux : republiés toutes les 2 h par CelesTrak, utilisables plusieurs jours.
  iss: {
    cacheTtlMs: 12 * HOUR,
    refreshMs: 6 * HOUR,
    liveMs: DAY,
    recentMs: 2 * DAY,
    discardAfterMs: 5 * DAY,
    kvWriteIntervalMs: 12 * HOUR,
  },
  airQuality: {
    cacheTtlMs: HOUR,
    refreshMs: 30 * MIN,
    liveMs: 30 * HOUR,
    recentMs: 48 * HOUR,
    discardAfterMs: 72 * HOUR,
  },
  // Contrôle sanitaire de l'eau : publication mensuelle, prélèvements espacés de plusieurs semaines.
  water: {
    cacheTtlMs: 12 * HOUR,
    refreshMs: 6 * HOUR,
    liveMs: DAY,
    recentMs: 3 * DAY,
    discardAfterMs: 30 * DAY,
    kvWriteIntervalMs: 12 * HOUR,
  },
  alerts: {
    cacheTtlMs: 10 * MIN,
    refreshMs: 5 * MIN,
    liveMs: 20 * MIN,
    recentMs: HOUR,
    discardAfterMs: DAY,
    kvWriteIntervalMs: 30 * MIN,
  },
  // Actualités municipales : quelques publications par semaine, relues chaque heure.
  news: {
    cacheTtlMs: HOUR,
    refreshMs: 30 * MIN,
    liveMs: 6 * HOUR,
    recentMs: DAY,
    discardAfterMs: 7 * DAY,
    kvWriteIntervalMs: HOUR,
  },
  events: { cacheTtlMs: HOUR, refreshMs: 30 * MIN, liveMs: 6 * HOUR, recentMs: DAY, discardAfterMs: 7 * DAY },
  roadworks: { cacheTtlMs: 6 * HOUR, refreshMs: HOUR, liveMs: DAY, recentMs: 3 * DAY, discardAfterMs: 14 * DAY },
  places: {
    cacheTtlMs: DAY,
    refreshMs: DAY,
    liveMs: 30 * DAY,
    recentMs: 90 * DAY,
    discardAfterMs: Number.POSITIVE_INFINITY,
  },
};

function parse(value: string | null): number | null {
  if (value === null) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

/** Âge de la donnée en ms : depuis `observedAt` si connu, sinon `fetchedAt`. null si inconnu. */
export function dataAgeMs(meta: FreshnessMeta, now: Date): number | null {
  const ref = parse(meta.observedAt) ?? parse(meta.fetchedAt);
  return ref === null ? null : Math.max(0, now.getTime() - ref);
}

export function computeFreshness(
  meta: FreshnessMeta | null,
  hasData: boolean,
  policy: FreshnessPolicy,
  now: Date,
): FreshnessStatus {
  if (!hasData || meta === null || parse(meta.fetchedAt) === null) return 'unavailable';
  const age = dataAgeMs(meta, now);
  if (age === null || age > policy.discardAfterMs) return 'unavailable';
  const expires = parse(meta.expiresAt);
  if (expires !== null && now.getTime() > expires) return 'stale';
  if (age <= policy.liveMs) return 'live';
  if (age <= policy.recentMs) return 'recent';
  return 'stale';
}
